import type {
	StructureDetailDto,
	StructureObjectDto,
} from '@/rtkApi/mddApiSliceGenerated'
import { SourceItem } from '@/rtkApi/mddApiSliceGeneratedV2'
import { findStructureForSourceItem } from '@/utils/source/findStructureForSourceItem'

import { type UpdateFieldParams } from '../types'

export const updateMappingSourceItemField = <K extends keyof SourceItem>({
	sourceItemFieldId,
	sourceItemFieldValue,
	mappingSourceTableRow,
}: UpdateFieldParams<K>) => {
	mappingSourceTableRow[sourceItemFieldId] = sourceItemFieldValue
}

export const clearObjectFields = (row: SourceItem) => {
	row.objectCustom = undefined
	row.objectId = undefined
	row.objectUuid = undefined
	row.objectCode = undefined
}

export const updateOwnerInfo = (
	row: SourceItem,
	users: Array<{ id?: number; name?: string }> | undefined,
	ownerId: SourceItem['ownerId'],
) => {
	if (!users) return

	const owner = users.find((user) => user.id === ownerId)
	if (owner) {
		row.ownerName = owner.name
	}
}

export const updateStructureFields = (
	row: SourceItem,
	structure: StructureObjectDto,
	nodeDetail?: StructureDetailDto,
) => {
	if (nodeDetail?.data) {
		const parsedNodeDetail = JSON.parse(nodeDetail.data)
		row.ownerId = parsedNodeDetail.ownerId
		row.ownerName = parsedNodeDetail.ownerName
	}

	row.objectId = structure.structureId
	row.objectUuid = structure.structureUuid
	row.objectCode = structure.code
}

export const findStructureByUuid = (
	structures: StructureObjectDto[] | undefined,
	uuid: StructureObjectDto['structureUuid'],
) => {
	if (!structures || typeof uuid !== 'string') return undefined
	return structures.find((structure) => structure.structureUuid === uuid)
}

const shouldSkipSourceItem = (sourceItem: SourceItem) => {
	return (
		sourceItem.objectType === 'SUB_SELECT' ||
		!sourceItem.objectType ||
		(!sourceItem.objectUuid &&
			sourceItem.objectId == null &&
			!sourceItem.objectCode)
	)
}

const isAlreadySynchronized = (
	sourceItem: SourceItem,
	matchingStructure: StructureObjectDto,
) => {
	return (
		sourceItem.objectId === matchingStructure.structureId &&
		sourceItem.objectUuid === matchingStructure.structureUuid
	)
}

export const syncSourceItemsWithSystemTables = (
	sourceItems: SourceItem[],
	systemTables: StructureObjectDto[],
) => {
	return sourceItems.map((sourceItem) => {
		if (shouldSkipSourceItem(sourceItem)) {
			return sourceItem
		}

		const matchingStructure = findStructureForSourceItem(
			sourceItem,
			systemTables,
		)

		if (
			!matchingStructure ||
			isAlreadySynchronized(sourceItem, matchingStructure)
		) {
			return sourceItem
		}

		return {
			...sourceItem,
			objectId: matchingStructure.structureId,
			objectUuid: matchingStructure.structureUuid,
			objectCode: matchingStructure.code,
		}
	})
}
